import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle, ExternalLink } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const PLATFORM_COLORS = {
  "Myntra": "bg-pink-100 text-pink-700 border-pink-200",
  "Nykaa": "bg-pink-100 text-pink-700 border-pink-200",
  "Ajio": "bg-orange-100 text-orange-700 border-orange-200",
  "Ajio Luxe": "bg-amber-100 text-amber-700 border-amber-200",
  "Blinkit": "bg-yellow-100 text-yellow-700 border-yellow-200",
  "Swiggy Instamart": "bg-orange-100 text-orange-700 border-orange-200",
  "Pharmeasy": "bg-green-100 text-green-700 border-green-200",
  "Aza Fashions": "bg-purple-100 text-purple-700 border-purple-200",
  "BigBasket": "bg-green-100 text-green-700 border-green-200",
  "Flipkart": "bg-blue-100 text-blue-700 border-blue-200",
  "Amazon": "bg-slate-100 text-slate-700 border-slate-200",
  "Zepto": "bg-violet-100 text-violet-700 border-violet-200",
  "Tata Cliq": "bg-indigo-100 text-indigo-700 border-indigo-200",
};

const getPlatformColor = (p) => PLATFORM_COLORS[p] || "bg-gray-100 text-gray-700 border-gray-200";

const OurClients = () => {
  const [clients, setClients] = useState([]);

  useEffect(() => {
    axios.get(`${API}/api/public/customer-logos`).then((r) => setClients(r.data || [])).catch(() => {});
  }, []);

  return (
    <section id="clients" className="py-20 bg-gradient-to-b from-white to-gray-50" data-testid="clients-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-200 rounded-full mb-4">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <span className="text-sm font-semibold text-green-700">Trusted by Leading Brands</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Our <span className="text-red-500">Success Stories</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Brands we've successfully onboarded on premium marketplaces and quick commerce platforms
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {clients.map((client) => {
            const name = client.brand_name || (client.name || "").replace(/\.[^.]+$/, "");
            const platforms = (client.platforms || "").split(",").map((p) => p.trim()).filter(Boolean);
            const card = (
              <>
                <div className="h-32 flex items-center justify-center mb-6 bg-gray-50 rounded-xl p-4 group-hover:bg-red-50 transition-colors">
                  <img
                    src={`${API}/api/public/customer-logos/${client.id}/image`}
                    alt={`${name} logo — launched by BrandBorn`}
                    loading="lazy"
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
                <div className="text-center">
                  <h3 className="text-xl font-bold text-gray-900 mb-1">{name}</h3>
                  {client.category && <p className="text-sm text-gray-500 mb-4">{client.category}</p>}
                  {platforms.length > 0 && (
                    <div className="flex flex-wrap justify-center gap-2">
                      {platforms.map((platform, pidx) => (
                        <span key={pidx} className={`px-3 py-1 text-xs font-semibold rounded-full border ${getPlatformColor(platform)}`}>
                          {platform}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="mt-4 flex items-center justify-center gap-1 text-green-600">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">Successfully Onboarded</span>
                  </div>
                  {client.has_case_study && client.slug && (
                    <div className="mt-2 text-xs font-bold text-red-500">Read Growth Story →</div>
                  )}
                </div>
              </>
            );
            return client.has_case_study && client.slug ? (
              <Link key={client.id} to={`/case-study/${client.slug}`} data-testid={`our-client-${client.id}`}
                className="group bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-red-200 cursor-pointer">
                {card}
              </Link>
            ) : (
              <div key={client.id} data-testid={`our-client-${client.id}`}
                className="group bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-red-200">
                {card}
              </div>
            );
          })}

          <div className="bg-gradient-to-br from-red-500 to-orange-500 rounded-2xl p-6 flex flex-col items-center justify-center text-white text-center min-h-[280px]">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mb-4">
              <ExternalLink className="w-8 h-8" />
            </div>
            <h3 className="text-xl font-bold mb-2">Your Brand Here?</h3>
            <p className="text-white/80 mb-4 text-sm">Join 1000+ brands we've launched on marketplaces</p>
            <a href="tel:+919464645491" className="bg-white text-red-500 px-6 py-2 rounded-full font-semibold hover:bg-gray-100 transition-colors">
              Get Started
            </a>
          </div>
        </div>

        <div className="mt-16 text-center">
          <div className="inline-flex flex-wrap justify-center items-center gap-4 md:gap-8 px-6 py-4 bg-gray-100 rounded-2xl">
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-red-500">1000+</span>
              <span className="text-sm text-gray-600">Brands Launched</span>
            </div>
            <div className="w-px h-8 bg-gray-300 hidden md:block" />
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-red-500">98%</span>
              <span className="text-sm text-gray-600">Success Rate</span>
            </div>
            <div className="w-px h-8 bg-gray-300 hidden md:block" />
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-red-500">70+</span>
              <span className="text-sm text-gray-600">Platforms</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OurClients;
